import { BaseApiClient } from "@/lib/api/baseApiClient.service";

import type {
	AvailableKlausurDTO,
	KorrekturOverviewDTO,
} from "./korrekturOverview.types";

/**
 * Service for handling Korrektur (correction) related API calls
 */
class KorrekturOverviewService {
	private static instance: KorrekturOverviewService;
	private baseApi: BaseApiClient;

	private constructor() {
		this.baseApi = BaseApiClient.getInstance();
	}

	public static getInstance(): KorrekturOverviewService {
		if (!KorrekturOverviewService.instance) {
			KorrekturOverviewService.instance = new KorrekturOverviewService();
		}
		return KorrekturOverviewService.instance;
	}

	/**
	 * Get available studentKlausuren for a user for a specific klausur
	 * @param klausurId - The id of the klausur
	 * @returns The korrekturUserKlausur data
	 */
	public async getAvailableStudentKlausurKorrekturen(
		klausurId: string,
	): Promise<KorrekturOverviewDTO[]> {
		const studentKlausuren: KorrekturOverviewDTO[] = await this.baseApi.get<
			KorrekturOverviewDTO[]
		>(`korrektur/get-available-student-klausur-korrekturen/${klausurId}`);
		return studentKlausuren;
	}

	/**
	 * Get available klausuren for a user for the dashboard for their current organization
	 * @returns The klausuren
	 */
	public async getAvailableKlausuren(
		organizationId: string,
	): Promise<AvailableKlausurDTO[]> {
		const klausuren: AvailableKlausurDTO[] = await this.baseApi.get<
			AvailableKlausurDTO[]
		>(`korrektur/get-available-klausuren/${organizationId}`);
		return klausuren;
	}

	/**
	 * Get klausur details by ID
	 * @param klausurId - The id of the klausur
	 * @returns The klausur details
	 */
	public async getKlausurById(klausurId: string): Promise<AvailableKlausurDTO> {
		const klausur: AvailableKlausurDTO =
			await this.baseApi.get<AvailableKlausurDTO>(
				`korrektur/get-klausur/${klausurId}`,
			);
		return klausur;
	}

	/**
	 * Create all korrekturen where user is korrektor
	 * @param klausurId - The id of the klausur
	 * @returns The korrekturen
	 */
	public async createKorrekturen(): Promise<void> {
		await this.baseApi.post<void, {}>(
			"korrektur/create-all-korrekturen-for-user",
			{},
		);
	}
}

export default KorrekturOverviewService;
